import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Drawer, Form, Space } from "antd";
import { CloseOutlined } from "@ant-design/icons";
import { useBoolean, useSafeState } from "ahooks";
import moment from "moment";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { PdFStepMapping } from "@containers/PdfReport";
import PdfForm from "@containers/PdfReport/PdfForm";
import { PdfProvider } from "@containers/PdfReport/context";
import useDateRange from "@/hooks/use-date-range";
import { safemomentmax, safemomentmin } from "@/containers/UtilityData/New-Summary/utils";
import { summaryChartClassnames } from "@/containers/UtilityData/New-Summary/components/summaryChartClassnames";
import { savingsChartClassnames } from "@/containers/UtilityData/New-Savings/components/savingsChartClassnames";
import ReportTable from "./ReportTable";
import ReportLoading from "./ReportLoading";
import ReportPreview from "./ReportPreview";
import ReportAiSummary from "./ReportAiSummary";
import { useAuth } from "@/hooks/use-auth";
import "@/containers/facility/AiAssistant/AiContextAction.css";

const getInitialValues = ({ facilityName, dateRange = {} }) => {
  const elecMaxDate = moment(dateRange?.date_range_elec_max || null);
  const gasMaxDate = moment(dateRange?.date_range_gas_max || null);
  const elecMinDate = moment(dateRange?.date_range_elec_min || null);
  const gasMinDate = moment(dateRange?.date_range_gas_min || null);
  const maxDate = safemomentmax(elecMaxDate, gasMaxDate);
  const minDate = safemomentmin(elecMinDate, gasMinDate);
  return {
    name: `${facilityName} - FacilityKPI report ${moment().format("YYYY_MM_DD HH_mm")}`,
    reportAudience: "Facility leadership",
    managementSummary: "",
    managementSummarySources: "",
    aiReportScope: "",
    utilityVisible: true,
    dashboardVisible: true,
    periodItem: summaryChartClassnames.map((item) => item.id),
    period: "12",
    endingMonth: maxDate.clone(),
    rangeMonth: [maxDate.clone().subtract(11, "months"), maxDate.clone()],
    allRangeMonth: [minDate.clone(), maxDate.clone()],
    savingAnalysisItem: savingsChartClassnames.map((item) => item.id),
    savingAnalysis: {
      elec_Option: "Baseline Average",
      gas_Option: "Baseline Average",
      elec_Baseline_Date: [
        elecMaxDate.clone().add(-23, "month").format("YYYY-MM"),
        elecMaxDate.clone().add(-12, "month").format("YYYY-MM"),
      ],
      elec_Post_Date: [
        elecMaxDate.clone().add(-11, "month").format("YYYY-MM"),
        elecMaxDate.clone().format("YYYY-MM"),
      ],
      gas_Baseline_Date: [
        gasMaxDate.clone().add(-23, "month").format("YYYY-MM"),
        gasMaxDate.clone().add(-12, "month").format("YYYY-MM"),
      ],
      gas_Post_Date: [
        gasMaxDate.clone().add(-11, "month").format("YYYY-MM"),
        gasMaxDate.clone().format("YYYY-MM"),
      ],
      gas_usedModuleValue: "Segments",
      elec_usedModuleValue: "Segments",
      gas_n_segments: 2,
      elec_n_segments: 2,
      breakpoints: [],
    },
  };
};

const Report = () => {
  const { companyId, facilityId } = useParams();
  const [isDrawerVisible, { setTrue: openDrawer, setFalse: closeDrawer }] = useBoolean(false);
  const [pdfFormValue, setPdfFormValue] = useSafeState({});
  const [previewUrl, setPreviewUrl] = useSafeState("");
  const [pdfStep, setPdfStep] = useState(PdFStepMapping.PDFForm);
  const goToFormStep = useCallback(() => setPdfStep(PdFStepMapping.PDFForm), []);
  const goToLoadingStep = useCallback(() => setPdfStep(PdFStepMapping.PDFLoading), []);
  const goToViewStep = useCallback(() => setPdfStep(PdFStepMapping.PDFView), []);
  const [form] = Form.useForm();
  const tableActionRef = useRef();
  const facilityName = useSelector((state) => state?.Facility?.selected?.record?.name || state?.Facility?.selected?.label || "Facility");
  const dateRange = useDateRange();
  const aiAllowed = useAuth("facility_ai_chat");
  const facilityAllowed = useAuth("facility_view");
  const reportAllowed = useAuth("customreport", 2);
  const [reportGeneration, setReportGeneration] = useState(0);
  const [reportDraftBusy, setReportDraftBusy] = useState(false);
  const claimedGeneration = useRef(0);
  const claimReportGeneration = useCallback((generation) => {
    if (generation <= claimedGeneration.current) return false;
    claimedGeneration.current = generation;
    return true;
  }, []);
  useEffect(() => {
    closeDrawer();
    form.resetFields();
    setPdfFormValue({});
    setPreviewUrl("");
    setReportGeneration(0);
    claimedGeneration.current = 0;
  }, [companyId, facilityId, closeDrawer, form, setPdfFormValue, setPreviewUrl]);

  const reloadTable = useCallback(() => {
    if (tableActionRef.current && typeof tableActionRef.current.reload === "function") {
      tableActionRef.current.reload();
    }
  }, []);

  const initForm = useCallback(
    (values) => {
      form.resetFields();
      form.setFieldsValue(values || getInitialValues({ facilityName, dateRange }));
    },
    [dateRange, facilityName, form]
  );

  const content = useMemo(() => {
    switch (pdfStep) {
      case PdFStepMapping.PDFLoading:
        return <ReportLoading />;
      case PdFStepMapping.PDFView:
        return <ReportPreview />;
      case PdFStepMapping.PDFForm:
      default:
        return <PdfForm />;
    }
  }, [pdfStep]);

  return (
    <PdfProvider
      value={{
        isDrawerVisible,
        openDrawer,
        closeDrawer,
        pdfFormValue,
        setPdfFormValue,
        pdfStep,
        goToFormStep,
        goToLoadingStep,
        goToViewStep,
        previewUrl,
        setPreviewUrl,
        reloadTable,
        tableActionRef,
        form,
        initForm,
        reportGeneration,
        claimReportGeneration,
        reportDraftBusy,
        setReportDraftBusy,
        reportSummaryEditor: <ReportAiSummary />,
      }}
    >
      <div style={{ padding: 10 }}>
        <Drawer
          title="Report Builder"
          extra={
            <Space>
              <CloseOutlined onClick={closeDrawer} />
            </Space>
          }
          open={isDrawerVisible}
          destroyOnClose
          placement="right"
          width="100%"
          onClose={closeDrawer}
        >
          {content}
        </Drawer>
        {aiAllowed && facilityAllowed && reportAllowed && <section className="facility-ai-context-action" aria-label="AI report generation">
          <div className="facility-ai-context-action__copy">
            <strong>Generate a report with AI</strong>
            <span>Uses the Report Builder period to draft an editable management summary with sources. Preview, save and download your report here.</span>
          </div>
          <Button type="primary" disabled={reportDraftBusy} onClick={() => {
            initForm();
            setPdfStep(PdFStepMapping.PDFForm);
            setReportGeneration(value => value + 1);
            openDrawer();
          }}>Generate report with AI</Button>
        </section>}
        <ReportTable />
      </div>
    </PdfProvider>
  );
};

export default React.memo(Report);
